import { Button, Divider, Grid, TextField, Typography } from '@mui/material';
import '../styles/Predictions.css';
import Card from '@mui/material/Card';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import SelectInput from './use-form-components/SelectInput';
import { AppRoutes, EndPoints } from '../axios/endPoints';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/store';
import { setPreviewParameters, svmPredict } from '../store/prediction-slice/prediction-slice';
import Loader from './common/Loader';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Header } from './common/Header';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';

const schema = (t : Function) => yup.object().shape({
    firstName: yup.string().nullable(),
    middleName: yup.string().nullable(),
    lastName: yup.string().nullable(),
    patientDateOfBirth: yup.date().nullable(),
    sex: yup.object().required(t('translations:PREDICTIONS_REQUIRED_FIELD')),
    age: yup.number().integer(t('translations:PREDICTIONS_AGE_VALIDATION'))
        .typeError(t('translations:PREDICTIONS_AGE_VALIDATION'))
        .min(15, t('translations:PREDICTIONS_AGE_VALIDATION'))
        .max(150, t('translations:PREDICTIONS_AGE_VALIDATION'))
        .required(t('translations:PREDICTIONS_AGE_VALIDATION')),
    weightKg: yup.number().integer(t('translations:PREDICTIONS_WEIGHT_KG_VALIDATION'))
        .typeError(t('translations:PREDICTIONS_WEIGHT_KG_VALIDATION'))
        .min(20, t('translations:PREDICTIONS_WEIGHT_KG_VALIDATION'))
        .max(180, t('translations:PREDICTIONS_WEIGHT_KG_VALIDATION'))
        .required(t('translations:PREDICTIONS_WEIGHT_KG_VALIDATION')),
    weightPounds: yup.number().integer(t('translations:PREDICTIONS_WEIGHT_POUNDS_VALIDATION'))
        .typeError(t('translations:PREDICTIONS_WEIGHT_POUNDS_VALIDATION'))
        .min(44, t('translations:PREDICTIONS_WEIGHT_POUNDS_VALIDATION'))
        .max(396, t('translations:PREDICTIONS_WEIGHT_POUNDS_VALIDATION'))
        .required(t('translations:PREDICTIONS_WEIGHT_POUNDS_VALIDATION')),
    height: yup.number().integer(t('translations:PREDICTIONS_HEIGHT_VALIDATION'))
        .typeError(t('translations:PREDICTIONS_HEIGHT_VALIDATION'))
        .min(30, t('translations:PREDICTIONS_HEIGHT_VALIDATION'))
        .max(230, t('translations:PREDICTIONS_HEIGHT_VALIDATION'))
        .required(t('translations:PREDICTIONS_HEIGHT_VALIDATION')),
    stonesize: yup.number()
        .typeError(t('translations:PREDICTIONS_STONE_SIZE_VALIDATION'))
        .positive(t('translations:PREDICTIONS_STONE_SIZE_VALIDATION'))
        .max(50, t('translations:PREDICTIONS_STONE_SIZE_VALIDATION'))
        .required(t('translations:PREDICTIONS_STONE_SIZE_VALIDATION')),
    stoneposition: yup.object().required(t('translations:PREDICTIONS_REQUIRED_FIELD')),
    fever: yup.object().required(t('translations:PREDICTIONS_REQUIRED_FIELD')),
    symptoms: yup.number().integer(t('translations:PREDICTIONS_SYMPTOMS_DURATION_VALIDATION'))
        .typeError(t('translations:PREDICTIONS_SYMPTOMS_DURATION_VALIDATION'))
        .min(0, t('translations:PREDICTIONS_SYMPTOMS_DURATION_VALIDATION'))
        .required(t('translations:PREDICTIONS_SYMPTOMS_DURATION_VALIDATION')),
    previousstone: yup.object().required(t('translations:PREDICTIONS_REQUIRED_FIELD')),
    previousurologic: yup.object().required(t('translations:PREDICTIONS_REQUIRED_FIELD'))
}).required();

const Predictions = () => 
{
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const loadingPredictions = useAppSelector((state) => state.predictions.loadingPredictions);
    const { watch, control, setValue, handleSubmit, formState: { errors } } = useForm({  
        mode: "onBlur", 
        resolver: yupResolver(schema(t)) 
    });

    function calculateBMI() {
        var weightKg = watch('weightKg');
        var height = watch('height');
        if(!height || height === 0 || height < 0)
            return t('translations:PREDICTIONS_BMI_NO_PARAMS');
        else if(!weightKg || weightKg === 0 || weightKg < 0)
            return t('translations:PREDICTIONS_BMI_NO_PARAMS');
        else {
            return (weightKg / ( (height / 100) * (height / 100) )).toFixed(2);
        }
    }

    function submitForm(data : any) {
        if(data) {
            var body = {
                firstName: data.firstName,
                middleName: data.middleName,
                lastName: data.lastName,
                patientDateOfBirth: data.patientDateOfBirth ? moment(data.patientDateOfBirth).format('DD/MM/YYYY') : null,
                sex: data.sex.id,
                age: data.age,
                weight: data.weightKg,
                height: data.height,
                stoneSizeInMilimeters: data.stonesize,
                stonePosition: data.stoneposition.id,
                fever: data.fever.id,
                symptomsDurationInHours: data.symptoms,
                previousStoneExplusion: data.previousstone.id,
                previousUrologicTreatment: data.previousurologic.id
            };
            dispatch(svmPredict(body)).then(({ payload } : any) => {
                if(payload && payload.success) { 
                    navigate(AppRoutes.PREDICTION_RESULTS);
                    dispatch(setPreviewParameters({...body,
                        sex: data.sex.name,
                        stonePosition: data.stoneposition.name,
                        fever: data.fever.name,
                        previousStoneExplusion: data.previousstone.name,
                        previousUrologicTreatment: data.previousurologic.name,
                        weightPounds: data.weightPounds
                    }))
                }
            })
        }
    }

    function onKeyPressIntValidation(event : any) {
        const charCode = event.which ? event.which : event.keyCode;

        if (charCode !== 8 && (charCode < 48 || charCode > 57)) 
        {
            event.preventDefault();
        } 
        else if (charCode === 48 && event?.target?.value?.length === 0) 
        {
            event.preventDefault();
        }
    } 

    function onPasteIntValidation(event : any) {
        const paste = event.clipboardData.getData('text');
        if (!/^\d+$/.test(paste) || paste[0] === '0') 
        {
            event.preventDefault();
        }
    }

    function onKeyPressFloatValidation(event: any) {
        const charCode = event.which ? event.which : event.keyCode;
        const charStr = String.fromCharCode(charCode);
    
        if (charCode === 8) {
            return;
        }
    
        const currentValue = event.target.value;
        if ((charCode >= 48 && charCode <= 57) || (charStr === '.' && currentValue.indexOf('.') === -1)) {
            return;
        }
    
        event.preventDefault();
    }

    function onPasteFloatValidation(event: any) {
        const paste = event.clipboardData.getData('text');
    
        const floatRegex = /^(?!0\d)\d*(\.\d+)?$/;
    
        if (!floatRegex.test(paste)) {
            event.preventDefault();
        }
    }

    useEffect(() => {
        document.title = t('translations:PREDICTIONS_PAGE_TITLE');
    }, [])

    useEffect(() => {
        const patientDOB = watch("patientDateOfBirth");
        if(patientDOB && moment(patientDOB).isValid()) {
            const yearsCalculated = moment().diff(patientDOB, 'years');
            setValue('age', yearsCalculated);
        }
    }, [watch("patientDateOfBirth")])
  
    return(
        <Card style={{marginTop: '5px'}}>
            { loadingPredictions ? <Loader open={loadingPredictions} /> : '' }
            <Header title={t('translations:PREDICTIONS_TITLE')} />
            <form onSubmit={handleSubmit(submitForm)}>
                <Grid container p={2}>
                    <Typography mb={1.5} mt={1} variant='h6' fontWeight="bold">{t('translations:PREDICTIONS_SUBTITLE')}</Typography>
                    <Grid container spacing={2}direction={{ xs: 'column', sm: 'row' }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={2}>
                            <Controller control={control} name="firstName" render={({ field }) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_PATIENT_FIRST_NAME_LABEL')} 
                                        type="text"
                                    />
                                )}
                            />
                        </Grid><Grid item xs={2}>
                            <Controller control={control} name="middleName" render={({ field }) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_PATIENT_MIDDLE_NAME_LABEL')} 
                                        type="text"
                                    />
                                )}
                            />
                        </Grid><Grid item xs={2}>
                            <Controller control={control} name="lastName" render={({ field }) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_PATIENT_LAST_NAME_LABEL')} 
                                        type="text"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller control={control} name="patientDateOfBirth" render={({ field }) => 
                                    (
                                        <DatePicker 
                                            disableFuture
                                            value={field.value ? moment(field.value) : null}
                                            onChange={(date) => field.onChange(date)}
                                            label={t('translations:PREDICTIONS_PATIENT_DATE_OF_BIRTH_LABEL')}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1} direction={{ xs: 'column', sm: 'row' }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={4}>
                            <SelectInput
                                url={EndPoints.GET_SEX_OPTIONS}
                                name={'sex'}
                                label={t('translations:PREDICTIONS_SEX_LABEL')}
                                idName={'id'}
                                labelName={'name'}
                                multiple={false}
                                disabled={false}
                                control={control}
                                translateName={true}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Controller control={control} name="age" render={({field, fieldState: { error }}) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        value={field.value ?? ''}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_AGE_LABEL')} 
                                        type="number"
                                        error={error !== undefined}
                                        helperText={error ? errors.age?.message : ''}
                                        inputProps={{ min: 1 }}
                                        onKeyPress={(event) => {         
                                            onKeyPressIntValidation(event);
                                        }}
                                        onPaste={(event) => {
                                            onPasteIntValidation(event);
                                        }}
                                        disabled={watch('patientDateOfBirth') != undefined}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1} direction={{ xs: 'column', sm: 'row' }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={2}>
                            <Controller control={control} name="weightPounds" render={({field, fieldState: { error }}) => 
                                ( 
                                    <TextField 
                                        {...field}
                                        fullWidth 
                                        value={field.value ?? ''}
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_WEIGHT_POUNDS_LABEL')} 
                                        type="number"
                                        error={error !== undefined}
                                        helperText={error ? errors.weightPounds?.message : ''}
                                        inputProps={{ min: 1 }}
                                        onKeyPress={(event) => {         
                                            onKeyPressIntValidation(event);
                                        }}
                                        onPaste={(event) => {
                                            onPasteIntValidation(event);
                                        }}
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            field.onChange(value);
                                            setValue('weightKg', parseInt((value / 2.20462).toFixed(2)));
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Controller control={control} name="weightKg" render={({field, fieldState: { error }}) => 
                                ( 
                                    <TextField 
                                        {...field}
                                        fullWidth 
                                        value={field.value ?? ''}
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_WEIGHT_KG_LABEL')} 
                                        type="number"
                                        error={error !== undefined}
                                        helperText={error ? errors.weightKg?.message : ''}
                                        inputProps={{ min: 1 }}
                                        onKeyPress={(event) => {         
                                            onKeyPressIntValidation(event);
                                        }}
                                        onPaste={(event) => {
                                            onPasteIntValidation(event);
                                        }}
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            field.onChange(value);
                                            setValue('weightPounds', parseInt((value * 2.20462).toFixed(2)));
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Controller control={control} name="height" render={({field, fieldState: { error }}) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_HEIGHT_LABEL')} 
                                        type="number"
                                        error={error !== undefined}
                                        helperText={error ? errors.height?.message : ''}
                                        inputProps={{ min: 1 }}
                                        onKeyPress={(event) => {         
                                            onKeyPressIntValidation(event);
                                        }}
                                        onPaste={(event) => {
                                            onPasteIntValidation(event);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant='subtitle1'>{t('translations:PREDICTIONS_BODY_MASS_INDEX_LABEL')}</Typography>
                            <Typography variant='subtitle1'>{calculateBMI()}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1} direction={{ xs: 'column', sm: 'row' }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}> 
                        <Grid item xs={4}>
                            <Controller control={control} name="stonesize" render={({field, fieldState: { error }}) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_STONE_SIZE_LABEL')} 
                                        type="number"
                                        error={error !== undefined}
                                        helperText={error ? errors.stonesize?.message : ''}
                                        inputProps={{ min: 0.0, step: 0.01 }}
                                        onKeyPress={(event) => {         
                                            onKeyPressFloatValidation(event);
                                        }}
                                        onPaste={(event) => {
                                            onPasteFloatValidation(event);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInput
                                url={EndPoints.GET_STONE_POSITION_OPTIONS}
                                name={'stoneposition'}
                                label={t('translations:PREDICTIONS_STONE_POSITION_LABEL')}
                                idName={'id'}
                                labelName={'name'}
                                multiple={false}
                                disabled={false}
                                control={control}
                                translateName={true}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1} direction={{ xs: 'column', sm: 'row' }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={4}>
                            <SelectInput
                                url={EndPoints.GET_FEVER_OPTIONS}
                                name={'fever'}
                                label={t('translations:PREDICTIONS_FEVER_LABEL')}
                                idName={'id'}
                                labelName={'name'}
                                multiple={false}
                                disabled={false}
                                control={control}
                                translateName={true}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Controller control={control} name="symptoms" render={({field, fieldState: { error }}) => 
                                ( 
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        variant="outlined" 
                                        label={t('translations:PREDICTIONS_SYMPTOMS_LABEL')}
                                        type="number"
                                        error={error !== undefined}
                                        helperText={error ? errors.symptoms?.message : ''}
                                        inputProps={{ min: 1 }}
                                        onKeyPress={(event) => {         
                                            onKeyPressIntValidation(event);
                                        }}
                                        onPaste={(event) => {
                                            onPasteIntValidation(event);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={1} direction={{ xs: 'column', sm: 'row' }} columnSpacing={{ xs: 1, sm: 2, md: 3 }}> 
                        <Grid item xs={4}>
                            <SelectInput
                                url={EndPoints.GET_PREVIOUS_STONE_EXPLUSION_OPTIONS}
                                name={'previousstone'}
                                label={t('translations:PREDICTIONS_PREVIOUS_STONE_EXPLUSION_LABEL')}
                                idName={'id'}
                                labelName={'name'}
                                multiple={false}
                                disabled={false}
                                control={control}
                                translateName={true}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInput
                                url={EndPoints.GET_PREVIOUS_UROLOGIC_TREATMENT_OPTIONS}
                                name={'previousurologic'}
                                label={t('translations:PREDICTIONS_PREVIOUS_TREATMENT_LABEL')}
                                idName={'id'}
                                labelName={'name'}
                                multiple={false}
                                disabled={false}
                                control={control}
                                translateName={true}
                            />
                        </Grid>
                    </Grid>
                    <Grid container mt={3} display="flex" alignItems="flex-start" justifyContent="flex-start">
                        <Button className="button-color" type="submit" size="large" variant="contained">{t('translations:PREDICTIONS_PREDICT_BUTTON_LABEL')}</Button>
                    </Grid>
                </Grid>
            </form>
        </Card>
    )
}

export default Predictions;